import { Component, Prop, Provide, Mixins } from "vue-property-decorator";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";

// Enums/Data
import { ProductTypes, RelatedProductsTypes } from "@/data/enums/products";
import { BrandConfigKeys, QUERY_PARAMS } from "@/data/constants";
import { PriceDisplayTypes } from "@/data/enums/prices";

// Types
import {
  ICrossSellProviderData,
  ICrossSellProviderMethods
} from "@/models/providers/crossSell";
import { IRelatedObject, IProductPrice } from "@/models/products";
import Context from "@/mixins/context";
import { getProductRoute, getCategoryRoute } from "@/data/shop/utils";
import _ from "@/boot/lodash";
import { IAccount } from "@/models/accounts";
import { IProduct } from "@/models/products";
import { IBasket } from "@/models/baskets";
import { ICurrency } from "@/models/constants";

@Component
export default class CrossSellProvider extends Mixins(
  RequestPromises,
  BrandsStore,
  Context
) {
  // Provide
  @Provide()
  public crossSellProviderData(): ICrossSellProviderData {
    return {
      crossSells: this.crossSells,
      hasMore: this.hasMore,
      isLoading: this.isLoading,
      isLoadingMore: this.isLoadingMore
    };
  }

  @Provide()
  public get crossSellProviderMethods(): ICrossSellProviderMethods {
    return {
      loadMore: this.loadMore
    };
  }

  // Props
  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ required: true })
  public basketId!: IBasket["id"];

  @Prop({ required: true })
  public accountId!: IAccount["id"];

  @Prop({ required: true })
  public currencyId!: ICurrency["id"];

  @Prop({ required: true })
  public coupons!: string[];

  @Prop({ required: true })
  public results!: { total: number; data: any[] };

  @Prop({ required: true })
  public getCrossSellsData!: Function;

  // Data
  public crossSells: IRelatedObject[] = [];

  public isLoading = false;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public limit = 4;
  public offset = 0;
  public hasMore = false;
  public isLoadingMore = false;

  public productTypes: ProductTypes[] = [
    ProductTypes.SINGLE_PRODUCT,
    ProductTypes.PRODUCT_BUNDLE
  ];

  public created() {
    this.updateCrossSells(this.results);
  }

  public updateCrossSells(results) {
    this.offset = this.offset + this.limit;
    this.hasMore = results.total > this.offset;
    this.crossSells = this.$_.concat(
      this.crossSells,
      this.mapItems(this.$_.get(results, "data", []))
    );
  }

  public mapItems(items) {
    return _.map(items, item => {
      const asLMFP = // asLMFP = as Lowest Monthly From Price
        this.$store.getters["brand/config"][
          BrandConfigKeys.PRICE_DISPLAY_TYPE
        ] === PriceDisplayTypes.MONTHLY_FROM;

      if (this.isProduct(item)) {
        // Map prices
        item.product.prices = this.$_.orderBy(
          _.map(item.product.prices, price => ({
            ..._.pick(price as IProductPrice, [
              "billing_cycle_months",
              "mixed_promotions",
              "monthly_price_from",
              "monthly_price_from_discounted_formatted",
              "monthly_price_from_formatted",
              "price",
              "price_discounted",
              "price_discounted_formatted",
              "price_formatted",
              "promotions"
            ]),
            ...this.$store.getters["data/catalogue/productTermSavings"](price)
          })),
          [
            term => {
              return asLMFP
                ? _.isNil(term.price_discounted)
                  ? (term.monthly_price_from as number)
                  : (term.price_discounted as number) /
                    (term.billing_cycle_months as number)
                : term.billing_cycle_months;
            }
          ],
          ["asc"]
        );
      }

      const term = _.has(item, "product.prices")
        ? (_.first(item.product.prices) as IProductPrice)
        : null;

      return _.merge(
        _.pick(item, ["id", "object_id", "product"]),
        {
          type: item.object_type,
          image: item.related_object.image,
          name: item.name_translated || item.name,
          description: item.description_translated || item.description,
          mixedPromotions: this.getMixedPromotionsData(
            _.get(item, "product.prices", [])
          ),
          promotionsByName: _.uniq(
            _.flatten(
              _.map(_.get(item, "product.prices", []), price =>
                _.map(
                  price.promotions || [],
                  promo => promo.name_translated || promo.name
                )
              )
            )
          ),
          label: item.label_translated || item.label,
          route: this.isProduct(item)
            ? getProductRoute(this, {
                [QUERY_PARAMS.PRODUCT_ID]: `${item.object_id}`,
                [QUERY_PARAMS.CROSS_SELL_PRODUCT]: 1
              })
            : getCategoryRoute(this, {
                [QUERY_PARAMS.CATEGORY_ID]: `${item.object_id}`
              })
        },
        term
          ? {
              ..._.pick(term, ["saving", "saving_formatted", "saving_summary"]),
              fromPrice: this.$store.getters[
                "data/catalogue/productTermFromPrice"
              ](term, asLMFP),
              oldFromPrice: (asLMFP
                ? _.get(term, "monthly_price_from_formatted")
                : _.get(term, "price_formatted")
              ).replace(/\.00\s?/, "")
            }
          : {}
      );
    });
  }

  public isProduct(item) {
    return item.object_type === RelatedProductsTypes.PRODUCT;
  }

  public isCategory(item) {
    return item.object_type === RelatedProductsTypes.CATEGORY;
  }

  public getMixedPromotionsData(productPrices) {
    const promotionsCount = this.$_.uniq(
      this.$_.flatten(
        this.$_.map(_.cloneDeep(productPrices), productPrice => {
          return this.$_.map(productPrice.promotions, promo => promo.id);
        })
      )
    ).length;

    return {
      hasMixedPromotions: _.some(productPrices, ["mixed_promotions", true]),
      promotionsCount
    };
  }

  public async loadMore() {
    this.isLoadingMore = true;
    try {
      const results = await this.getCrossSellsData(
        this.productId,
        this.limit,
        this.offset
      );
      this.updateCrossSells(results);
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoadingMore = false;
    }
  }
}
